import { Box, TextField, Typography } from '@mui/material';
import { ChangeEvent, useCallback, useContext, useState } from 'react';

import CommentsWrap from '@/components/comments_components/CommentsWrap';
import { CurrentApplicationContext } from '@/Contexts/CurrentApplicationContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import useAutosave from '@/hooks/useAutosave';
import { QuestionProps } from '@/models/Question';
import { maxIntValue } from '@/utils/numberUtil';

import SavedIndicator from '../SavedIndicator';
import useNumberFormatter from '@/hooks/useNumberFormatter';
import { logError } from '@/utils/errorHandlerUtil';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';

function IntegerQuestionType({ question }: { question: QuestionProps }) {
  const { answers, canEdit, saveApplicationAnswer2 } = useContext(CurrentApplicationContext);
  const { translatedText } = useContext(LocaleContext);
  const { formatter, parseInteger } = useNumberFormatter();
  const { setAlert } = useContext(SnackbarContext);

  const questionAnswer = answers[question.id];
  const formattedQuestionAnswerValue =
    questionAnswer != null && questionAnswer.responseInt != null
      ? formatter.format(questionAnswer.responseInt)
      : '';
  const [currentValue, setCurrentValue] = useState(formattedQuestionAnswerValue);
  const { intValue, valid: validFormat } = parseInteger(currentValue);

  const handleFocused = () => {
    setCurrentValue(validFormat ? (intValue === null ? '' : String(intValue)) : currentValue);
  };

  const handleBlurred = () => {
    const { intValue: parsed, valid: parsedValid } = parseInteger(currentValue);
    setCurrentValue(parsedValid ? (parsed === null ? '' : formatter.format(parsed)) : currentValue);
  };

  const [saveFailed, setSaveFailed] = useState(false);

  const maxValue = maxIntValue;

  const missingRequiredValue = question.questionNeeded && currentValue === '';
  const negativeValue = intValue !== null && intValue < 0;
  const exceededMaxValue = maxValue !== null && intValue !== null && intValue > maxValue;
  const outOfRangeValue = negativeValue || exceededMaxValue;
  const saveableValue = !(outOfRangeValue || !validFormat);

  const hasError = missingRequiredValue || outOfRangeValue || !validFormat;

  const saveValue = useCallback(
    async (v: number | null): Promise<void> => {
      setSaveFailed(false);
      try {
        await saveApplicationAnswer2(question, v);
      } catch (error) {
        logError(`Error saving response in integer type question.`, error);

        setAlert({
          severity: 'error',
          message: 'Error saving response - please try again.',
        });
        setSaveFailed(true);
      }
    },
    [question, saveApplicationAnswer2, setAlert]
  );

  const { saved, isSaving, markTouched, triggerSave } = useAutosave<number | null>(
    intValue,
    async (v) => {
      if (!saveableValue) return;
      return await saveValue(v);
    },
    1500
  );

  const handleChanged = (e: ChangeEvent<HTMLInputElement>) => {
    markTouched();
    setCurrentValue(e.target.value);
  };

  return (
    <Box
      display='flex'
      width='100%'
      justifyContent='space-between'
      alignItems='start'
      id={`integer-question-${question.id}`}
    >
      <Box display='flex' flexDirection='row' justifyContent='flex-start' sx={{ width: '100%' }}>
        <Box display='flex' flexDirection='column' alignItems='center'>
          <TextField
            id={question.id.toString()}
            label={question.questionText}
            sx={{
              minWidth: '500px',
              maxWidth: '500px',
            }}
            variant='outlined'
            disabled={!canEdit}
            onFocus={handleFocused}
            onBlur={handleBlurred}
            onWheel={() => {}}
            onChange={handleChanged}
            required={question.questionNeeded}
            value={currentValue}
            error={hasError}
          />

          {!saveableValue && (
            <div
              style={{
                minWidth: '500px',
                maxWidth: '500px',
              }}
            >
              <Typography variant='subtitle3' color='error'>
                {translatedText.integerQuestion.invalidInteger}
              </Typography>
            </div>
          )}

          <Box
            sx={{
              minWidth: '500px',
              maxWidth: '500px',
            }}
          >
            <Typography variant='subtitle3' color='textSecondary'>
              {question.questionNote}
            </Typography>
          </Box>
        </Box>
        <SavedIndicator
          saved={saved}
          saveFailed={saveFailed}
          isSaving={isSaving.current}
          saveFn={triggerSave}
          sx={{ alignSelf: 'flex-end' }}
        />
      </Box>
      <CommentsWrap question={question} />
    </Box>
  );
}

export default IntegerQuestionType;
